/**
 * Visual editor for a button's `on_click` action chain (ADR-0010 P7c2 · S12).
 *
 * Mirrors the runtime in `lib/pageActions.ts`: an ordered list of actions,
 * each `{type, ...args}`. The inspector dispatches here for any prop marked
 * `x-tray-action-chain`. CLIENT actions (set-var / navigate / refresh / toast)
 * plus SERVER actions (create_row / update_row / delete_row) that write to a
 * Data Table — gated by the role's write policy server-side. Per-type arg
 * fields accept `{{ }}` templates (rendered at runtime against the page scope).
 *
 * ADR-0043 F4: every LEAF action also gets On-success / On-failure continuation
 * sub-chains (the per-action verb/SDK model) — run after the action completes /
 * fails (`{{ $page.error }}` bound in the failure branch).
 *
 * ADR-0044 U-track "editor azioni pulito" (Appsmith #3/#5): each action is a
 * COLLAPSED card showing only its plain-Italian summary (`summarizeAction`) —
 * click to expand the config (type + args + confirm + the single collapsible
 * **Callback** section folding On-success/On-failure). A freshly-added action
 * auto-expands. The data SHAPE is unchanged; this is presentation + i18n only.
 */
import { ChevronDown, ChevronRight } from "lucide-react";
import { useRef, useState } from "react";

import { summarizeAction } from "@/lib/actionSummary";
import {
  ACTION_TYPES,
  CONTROL_ACTION_TYPES,
  SERVER_ACTION_TYPES,
  type ActionType,
  type PageAction,
} from "@/lib/pageActions";
import type { ResolvedPageInput } from "@/lib/pageInputs";
import type { ResourceQuery } from "@/lib/resourceKinds";

import { PageInputArgsEditor } from "./PageInputArgsEditor";
import { FloatingPopover } from "./FloatingPopover";
import { ResourcePicker } from "./ResourcePicker";

/** Human labels for the action-type select (leaf + control flow). Italian —
 *  the page builder is operator-facing and IT-first. */
const ACTION_LABELS: Record<string, string> = {
  set_variable: "Imposta variabile",
  navigate: "Vai a una pagina",
  open_drawer: "Apri pagina in drawer",
  open_overlay: "Apri overlay (modale / drawer)",
  close_overlay: "Chiudi overlay (restituisci risultato)",
  open_modal: "Apri modale / drawer (componente)",
  close_modal: "Chiudi modale / drawer (componente)",
  refresh: "Ricarica le query",
  toast: "Mostra un messaggio",
  create_row: "Crea riga",
  update_row: "Aggiorna riga",
  delete_row: "Elimina riga",
  run_workflow: "Esegui workflow",
  run_query: "Esegui query / operazione (.run)",
  print_report: "Stampa report (PDF)",
  run_js: "Esegui funzione JS",
  global_value: "Salva nello store globale",
  global_remove: "Rimuovi dallo store",
  global_clear: "Svuota lo store",
  if: "Se / altrimenti (condizione)",
  try: "Prova / in caso di errore",
};

/** Every USER-SELECTABLE action type, leaf + control flow. `run_js_inline` is the
 *  JS-mode storage shape (ADR-0044 U-track), authored via the field's `JS` toggle —
 *  NOT a card-pickable action — so it's excluded from the type select + add menu. */
const ALL_ACTION_TYPES: readonly string[] = [
  ...ACTION_TYPES.filter((t) => t !== "run_js_inline"),
  ...CONTROL_ACTION_TYPES,
];
const CONTROL_SET: ReadonlySet<string> = new Set(CONTROL_ACTION_TYPES);

export interface ActionChainEditorProps {
  value: unknown;
  /** Declared page-variable names — power the `set_variable` name select. */
  varNames: string[];
  /** Overlay pages (slug + title + default mode) — power the `open_overlay`
   *  page picker (ADR-0023). Empty/omitted ⇒ the picker shows a "create one" hint. */
  overlayPages?: {
    slug: string;
    title: string;
    mode: "modal" | "drawer";
    inputs?: ResolvedPageInput[];
  }[];
  /** In-page modal/drawer components (id + label) — power the `open_modal` /
   *  `close_modal` target picker (ADR-0045 R2). */
  modalTargets?: { id: string; label: string }[];
  /** App tables (id + name) — power the server-action table picker (S12). */
  tables: { id: string; name: string }[];
  /** Columns per table id — power the field-values column picker (S13);
   *  no more free-text column names. */
  columnsByTable: Record<string, string[]>;
  /** Runnable workflows (id + name) — power the run-workflow picker (S14).
   *  Only workflows that opted in with a `button_clicked` trigger. */
  workflows: { id: string; name: string }[];
  /** Declared page-query names — power the `run_query` `.run()` picker (F3).
   *  Optional (defaults to none) so an embedding without queries still mounts. */
  queryNames?: string[];
  /** Declared page queries WITH their `source_kind` — drives the SEGMENTED
   *  `run_query` resource picker (Tabelle / Dataset / HTTP / JS). Falls back to
   *  `queryNames` (kind `other`) when absent. */
  queries?: ResourceQuery[];
  /** JS Object handler refs `<Name>.<method>` (ADR-0044 J2) — power the
   *  `run_js` function picker. Optional (defaults to none). */
  jsFunctions?: string[];
  /** Linked reports (id + label) — power the `print_report` picker
   *  (ADR-0060 S5). Optional (defaults to none → free-text id). */
  reportOptions?: { id: string; label: string }[];
  onChange: (actions: PageAction[]) => void;
}

/** The shared authoring context a nested editor (sub-chain / continuation)
 *  needs — everything ActionChainEditor takes except `value`/`onChange`. */
type AuthoringCtx = Pick<
  ActionChainEditorProps,
  | "varNames"
  | "overlayPages"
  | "modalTargets"
  | "tables"
  | "columnsByTable"
  | "workflows"
  | "queryNames"
  | "queries"
  | "jsFunctions"
  | "reportOptions"
>;

const input = "w-full rounded border border-border bg-background px-2 py-1 text-sm";
const fieldLabel = "text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70";

export function ActionChainEditor({
  value,
  varNames,
  overlayPages = [],
  modalTargets = [],
  tables,
  columnsByTable,
  workflows,
  queryNames = [],
  queries,
  jsFunctions = [],
  reportOptions = [],
  onChange,
}: ActionChainEditorProps) {
  // Tolerate malformed AST: anything non-array starts an empty chain.
  const actions: PageAction[] = Array.isArray(value)
    ? (value.filter((a) => a && typeof a === "object") as PageAction[])
    : [];

  // The index that was just added (auto-expands its card so it's ready to
  // configure, Appsmith-style). Bumped by `add`, harmless after the next render.
  const [justAdded, setJustAdded] = useState<number | null>(null);

  const patch = (i: number, next: PageAction) =>
    onChange(actions.map((a, j) => (j === i ? next : a)));
  const remove = (i: number) => onChange(actions.filter((_, j) => j !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= actions.length) return;
    const next = [...actions];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = (type: string) => {
    setJustAdded(actions.length);
    onChange([...actions, { type }]);
  };

  // The authoring context the nested editors (if/try sub-chains + the F4
  // On-success / On-failure continuations) need to render their own pickers.
  const authoring: AuthoringCtx = {
    varNames,
    overlayPages,
    modalTargets,
    tables,
    columnsByTable,
    workflows,
    queryNames,
    queries,
    jsFunctions,
    reportOptions,
  };

  return (
    <div className="flex flex-col gap-1.5" data-testid="action-chain-editor">
      {actions.length === 0 && (
        <p className="text-xs italic text-muted-foreground">Nessuna azione.</p>
      )}
      {actions.map((action, i) => (
        <ActionRow
          key={i}
          action={action}
          index={i}
          total={actions.length}
          defaultOpen={i === justAdded}
          authoring={authoring}
          onChange={(next) => patch(i, next)}
          onRemove={() => remove(i)}
          onMove={(dir) => move(i, dir)}
        />
      ))}
      <ActionAddMenu onPick={add} />
    </div>
  );
}

/** Categorised "+ Aggiungi azione" menu (Appsmith #5): the action types grouped
 *  by intent (Dati / Logica / Navigazione / Feedback / Stato / Workflow) in a
 *  popover, instead of blindly appending a `refresh`. Picking one appends a
 *  fresh action of that type (its args editor fills the rest). */
const ADD_GROUPS: { label: string; types: string[] }[] = [
  { label: "Dati", types: ["run_query", "print_report", "create_row", "update_row", "delete_row", "refresh"] },
  { label: "Logica", types: ["run_js", "if", "try"] },
  {
    label: "Navigazione",
    types: [
      "navigate",
      "open_drawer",
      "open_overlay",
      "close_overlay",
      "open_modal",
      "close_modal",
    ],
  },
  { label: "Feedback", types: ["toast"] },
  { label: "Stato", types: ["set_variable", "global_value", "global_remove", "global_clear"] },
  { label: "Workflow", types: ["run_workflow"] },
];

function ActionAddMenu({ onPick }: { onPick: (type: string) => void }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="relative w-fit">
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        className="rounded border border-dashed border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
        onClick={() => setOpen((o) => !o)}
      >
        + Aggiungi azione
      </button>
      {/* Rendered in a PORTAL via FloatingPopover so the inspector's overflow
          can't clip it (the old `absolute` menu "opened nothing"); placed to the
          left of the button, vertically centered (Appsmith-style). */}
      <FloatingPopover anchorRef={btnRef} open={open} onClose={() => setOpen(false)}>
        <div
          role="menu"
          className="portal-pop max-h-[60vh] w-64 overflow-auto rounded-md border border-border bg-elevated p-1 shadow-xl"
        >
          {ADD_GROUPS.map((g) => (
            <div key={g.label} className="py-0.5">
              <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                {g.label}
              </p>
              {g.types.map((t) => (
                <button
                  key={t}
                  type="button"
                  role="menuitem"
                  className="w-full rounded px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent/10 hover:text-foreground"
                  onClick={() => {
                    onPick(t);
                    setOpen(false);
                  }}
                >
                  {ACTION_LABELS[t]}
                </button>
              ))}
            </div>
          ))}
        </div>
      </FloatingPopover>
    </div>
  );
}

/** One action: a collapsed card showing the plain-Italian summary (click the
 *  header to expand the config). Expanded = type select + args + confirm + the
 *  single collapsible Callback section. A control action (if/try) owns no leaf
 *  confirm/callback — it branches itself. */
function ActionRow({
  action,
  index,
  total,
  defaultOpen,
  authoring,
  onChange,
  onRemove,
  onMove,
}: {
  action: PageAction;
  index: number;
  total: number;
  defaultOpen: boolean;
  authoring: AuthoringCtx;
  onChange: (next: PageAction) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isControl = CONTROL_SET.has(action.type);

  return (
    <div className="rounded border border-border bg-surface">
      <div className="flex items-center gap-1 px-2 py-1.5">
        <button
          type="button"
          aria-label={`${open ? "Comprimi" : "Espandi"} azione ${index + 1}`}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <span className="truncate text-xs text-foreground">
            {summarizeAction(action, authoring.overlayPages ?? [])}
          </span>
        </button>
        <button
          type="button"
          aria-label="Move up"
          className="px-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
          disabled={index === 0}
          onClick={() => onMove(-1)}
        >
          ↑
        </button>
        <button
          type="button"
          aria-label="Move down"
          className="px-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
          disabled={index === total - 1}
          onClick={() => onMove(1)}
        >
          ↓
        </button>
        <button
          type="button"
          aria-label="Remove action"
          className="px-1 text-danger"
          onClick={onRemove}
        >
          ✕
        </button>
      </div>

      {open && (
        <div className="flex flex-col gap-2 border-t border-border/60 px-2 py-2">
          <div className="flex flex-col gap-1">
            <span className={fieldLabel}>Azione</span>
            <select
              aria-label="Action type"
              className={input}
              value={ALL_ACTION_TYPES.includes(action.type) ? action.type : "refresh"}
              // Reset args when the type changes — each type owns its keys.
              onChange={(e) => onChange({ type: e.target.value })}
            >
              {ALL_ACTION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {ACTION_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          <ActionArgs action={action} authoring={authoring} onChange={onChange} />

          {/* Optional confirmation prompt + the single collapsible Callback
              section (On-success / On-failure, ADR-0043 F4). Control-flow
              actions own neither — they branch themselves. */}
          {!isControl && (
            <>
              <div className="flex flex-col gap-1">
                <span className={fieldLabel}>Conferma</span>
                <input
                  aria-label="Confirm message"
                  className={input}
                  placeholder="Messaggio di conferma (opzionale, es. Eliminare?)"
                  value={String(action.confirm ?? "")}
                  onChange={(e) => onChange({ ...action, confirm: e.target.value })}
                />
              </div>
              <Callbacks action={action} authoring={authoring} onChange={onChange} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** The single collapsible **Callback** disclosure (Appsmith #3): one row that
 *  summarises how many continuations are set, expanding to the On-success /
 *  On-failure sub-chains. Folds the two F4 continuations under one header so the
 *  common (no-callback) case stays a single quiet line. */
function Callbacks({
  action,
  authoring,
  onChange,
}: {
  action: PageAction;
  authoring: AuthoringCtx;
  onChange: (next: PageAction) => void;
}) {
  const count = (v: unknown) => (Array.isArray(v) ? v.filter((a) => a && typeof a === "object").length : 0);
  const total = count(action.on_success) + count(action.on_failure);
  const [open, setOpen] = useState(total > 0);

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        aria-label="Callback"
        aria-expanded={open}
        className="flex items-center gap-1.5 text-left"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? (
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
        )}
        <span className={fieldLabel}>Callback</span>
        <span className="text-[11px] text-muted-foreground">
          {total === 0 ? "nessuna" : `${total} ${total === 1 ? "azione" : "azioni"}`}
        </span>
      </button>
      {open && (
        <div className="flex flex-col gap-1 pl-1">
          <Continuation
            label="In caso di successo"
            value={action.on_success}
            authoring={authoring}
            onChange={(acts) => onChange({ ...action, on_success: acts })}
          />
          <Continuation
            label="In caso di errore (legge {{ $page.error }})"
            value={action.on_failure}
            authoring={authoring}
            onChange={(acts) => onChange({ ...action, on_failure: acts })}
          />
        </div>
      )}
    </div>
  );
}

/** A collapsible On-success / On-failure continuation sub-chain (ADR-0043 F4).
 *  Hidden behind a "+ label" toggle until it holds actions or the author opens
 *  it, so the common (no-continuation) case stays uncluttered. The nested
 *  `ActionChainEditor` inherits the full authoring context. */
function Continuation({
  label,
  value,
  authoring,
  onChange,
}: {
  label: string;
  value: unknown;
  authoring: AuthoringCtx;
  onChange: (actions: PageAction[]) => void;
}) {
  const acts: PageAction[] = Array.isArray(value)
    ? (value.filter((a) => a && typeof a === "object") as PageAction[])
    : [];
  const [open, setOpen] = useState(acts.length > 0);

  if (!open) {
    return (
      <button
        type="button"
        className="w-fit text-[11px] text-muted-foreground hover:text-foreground"
        onClick={() => setOpen(true)}
      >
        + {label}
      </button>
    );
  }
  return (
    <div className="rounded border-l-2 border-border/70 pl-2">
      <div className="mb-0.5 flex items-center justify-between">
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
          {label}
        </span>
        {acts.length === 0 && (
          <button
            type="button"
            aria-label={`Collapse ${label}`}
            className="px-1 text-muted-foreground hover:text-foreground"
            onClick={() => setOpen(false)}
          >
            –
          </button>
        )}
      </div>
      <ActionChainEditor value={value} {...authoring} onChange={onChange} />
    </div>
  );
}

/** Per-type argument fields. Keeps each action's keys self-describing. */
function ActionArgs({
  action,
  authoring,
  onChange,
}: {
  action: PageAction;
  authoring: AuthoringCtx;
  onChange: (next: PageAction) => void;
}) {
  const {
    varNames,
    overlayPages = [],
    modalTargets = [],
    tables,
    columnsByTable,
    workflows,
    queryNames = [],
    queries,
    jsFunctions = [],
    reportOptions = [],
  } = authoring;

  // Nested sub-chains get the SAME authoring context (vars/tables/ops/…).
  const nested = (key: string, label: string) => (
    <div className="mt-1 rounded border-l-2 border-border/70 pl-2">
      <span className={fieldLabel}>{label}</span>
      <ActionChainEditor
        value={action[key]}
        {...authoring}
        onChange={(acts) => onChange({ ...action, [key]: acts })}
      />
    </div>
  );

  if (action.type === "if") {
    return (
      <div className="flex flex-col gap-1">
        <input
          aria-label="Condition"
          className={`${input} font-mono text-xs`}
          placeholder="condizione — es. {{ $page.qty > 0 }}"
          value={String(action.condition ?? "")}
          onChange={(e) => onChange({ ...action, condition: e.target.value })}
        />
        {nested("then", "Allora")}
        {nested("else", "Altrimenti")}
      </div>
    );
  }
  if (action.type === "try") {
    return (
      <div className="flex flex-col gap-1">
        {nested("do", "Esegui")}
        {nested("catch", "In caso di errore (legge {{ $page.error }})")}
      </div>
    );
  }
  if (action.type === "set_variable") {
    return (
      <div className="flex flex-col gap-1">
        <input
          aria-label="Variable name"
          className={input}
          list="action-var-names"
          placeholder="nome variabile"
          value={String(action.name ?? "")}
          onChange={(e) => onChange({ ...action, name: e.target.value })}
        />
        <datalist id="action-var-names">
          {varNames.map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
        <input
          aria-label="Variable value"
          className={input}
          placeholder="valore (supporta {{ }})"
          value={String(action.value ?? "")}
          onChange={(e) => onChange({ ...action, value: e.target.value })}
        />
      </div>
    );
  }
  if (action.type === "navigate") {
    return (
      <input
        aria-label="Navigate target"
        className={input}
        placeholder="/portal/app/p/pagina (supporta {{ }})"
        value={String(action.to ?? "")}
        onChange={(e) => onChange({ ...action, to: e.target.value })}
      />
    );
  }
  if (action.type === "open_drawer") {
    return (
      <input
        aria-label="Drawer page slug"
        className={input}
        placeholder="slug della pagina da aprire in drawer (es. deal_new)"
        value={String(action.page ?? "")}
        onChange={(e) => onChange({ ...action, page: e.target.value })}
      />
    );
  }
  if (action.type === "open_modal" || action.type === "close_modal") {
    // ADR-0045 R2. Pick an in-page modal/drawer COMPONENT by node id. A free-text
    // fallback keeps it usable when the page has none yet (or the id is a binding).
    return (
      <div className="flex flex-col gap-1.5">
        {modalTargets.length > 0 ? (
          <select
            aria-label="Modal target"
            className={input}
            value={String(action.target ?? "")}
            onChange={(e) => onChange({ ...action, target: e.target.value })}
          >
            <option value="">— scegli una modale / drawer —</option>
            {modalTargets.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            aria-label="Modal target"
            className={input}
            placeholder="id del nodo modale / drawer (o {{ espressione }})"
            value={String(action.target ?? "")}
            onChange={(e) => onChange({ ...action, target: e.target.value })}
          />
        )}
        {modalTargets.length === 0 && (
          <p className="text-[11px] leading-snug text-muted-foreground">
            Nessun componente Modale/Drawer in questa pagina. Aggiungine uno dalla
            palette (categoria Layout), poi selezionalo qui.
          </p>
        )}
      </div>
    );
  }
  if (action.type === "open_overlay") {
    // ADR-0023. Pick an OVERLAY page, optionally override its mode, pass a
    // `context` map (→ read-only `$input`), and run an `on_close` sub-chain in
    // the opener scope (with `{{ $result }}` bound) when the overlay closes.
    return (
      <div className="flex flex-col gap-1.5">
        <select
          aria-label="Overlay page"
          className={input}
          value={String(action.page ?? "")}
          onChange={(e) => onChange({ ...action, page: e.target.value })}
        >
          <option value="">— scegli una pagina overlay —</option>
          {overlayPages.map((p) => (
            <option key={p.slug} value={p.slug}>
              {p.title} ({p.mode})
            </option>
          ))}
        </select>
        {overlayPages.length === 0 && (
          <p className="text-[11px] leading-snug text-muted-foreground">
            Nessuna pagina overlay. Creane una di tipo “Modale” o “Drawer” dal
            pannello Pagine, poi selezionala qui.
          </p>
        )}
        <select
          aria-label="Overlay mode"
          className={input}
          value={action.mode === "modal" || action.mode === "drawer" ? action.mode : ""}
          onChange={(e) => {
            const m = e.target.value;
            // "" = use the page's default presentation (drop the override).
            const next = { ...action } as PageAction;
            if (m) next.mode = m;
            else delete next.mode;
            onChange(next);
          }}
        >
          <option value="">Predefinito (modalità della pagina)</option>
          <option value="modal">Modale</option>
          <option value="drawer">Drawer</option>
        </select>
        {/* context → read-only `$input`. Guided form from the TARGET overlay's
            resolved input contract (declared or auto-discovered) so the opener
            sees exactly which params the overlay expects (ADR-0045 R4-inputs). */}
        <span className={fieldLabel}>Context → $input</span>
        <PageInputArgsEditor
          value={action.context}
          inputs={overlayPages.find((p) => p.slug === action.page)?.inputs ?? []}
          onChange={(context) => onChange({ ...action, context })}
        />
        {nested("on_close", "Alla chiusura (gira in questa pagina, legge {{ $result }})")}
      </div>
    );
  }
  if (action.type === "close_overlay") {
    // ADR-0023. Return a `result` map to the opener's on_close (as `$result`).
    return (
      <div className="flex flex-col gap-1">
        <span className={fieldLabel}>Result → $result</span>
        <ValuesEditor
          value={action.result}
          columns={[]}
          onChange={(result) => onChange({ ...action, result })}
        />
      </div>
    );
  }
  if (action.type === "toast") {
    return (
      <input
        aria-label="Toast message"
        className={input}
        placeholder="messaggio (supporta {{ }})"
        value={String(action.message ?? "")}
        onChange={(e) => onChange({ ...action, message: e.target.value })}
      />
    );
  }
  if (action.type === "run_workflow") {
    return (
      <div className="flex flex-col gap-1.5">
        <select
          aria-label="Workflow"
          className={input}
          value={String(action.workflow ?? "")}
          onChange={(e) => onChange({ ...action, workflow: e.target.value })}
        >
          <option value="">— scegli un workflow —</option>
          {workflows.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
        {workflows.length === 0 && (
          <p className="text-[11px] leading-snug text-muted-foreground">
            Nessun workflow eseguibile. Aggiungi un trigger “Button clicked” a un
            workflow dell’app per poterlo lanciare da una pagina.
          </p>
        )}
        {/* Free-form payload (key → {{ }} template) passed to the workflow. */}
        <ValuesEditor
          value={action.values}
          columns={[]}
          onChange={(values) => onChange({ ...action, values })}
        />
      </div>
    );
  }
  if (action.type === "run_query") {
    // Segmented resource picker (Tabelle / Dataset / HTTP / JS), falling back to
    // bare names when only `queryNames` was threaded.
    const pickerQueries: ResourceQuery[] = queries ?? queryNames.map((name) => ({ name }));
    return (
      <div className="flex flex-col gap-1.5">
        <ResourcePicker
          queries={pickerQueries}
          value={String(action.query ?? "")}
          align="left"
          onSelect={(name) => onChange({ ...action, query: name })}
        />
        <p className="text-[11px] leading-snug text-muted-foreground">
          Riesegue la query sullo stato corrente della pagina. Usalo per lanciare
          una query con ciclo di vita “manuale”.
        </p>
      </div>
    );
  }
  if (action.type === "print_report") {
    // ADR-0060 S5 — pick a report of a LINKED Type; the end-user's role must
    // also be granted it (Membri e ruoli), or the render 404s.
    return (
      <div className="flex flex-col gap-1.5">
        {reportOptions.length > 0 ? (
          <select
            aria-label="Report"
            className={input}
            value={String(action.report ?? "")}
            onChange={(e) => onChange({ ...action, report: e.target.value })}
          >
            <option value="">— scegli un report —</option>
            {reportOptions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            className={input}
            placeholder="id report (collega un tipo in Core → Report)"
            value={String(action.report ?? "")}
            onChange={(e) => onChange({ ...action, report: e.target.value })}
          />
        )}
        <p className="text-[11px] leading-snug text-muted-foreground">
          Scarica il PDF del report. Il ruolo dell'utente deve avere il grant in
          «Membri e ruoli → Report». I parametri sono template {"{{ }}"}.
        </p>
        {/* params → the Type's provider input (key → {{ }} template). */}
        <ValuesEditor
          value={action.params}
          columns={[]}
          onChange={(params) => onChange({ ...action, params })}
        />
      </div>
    );
  }
  if (action.type === "run_js") {
    // ADR-0044 J2. Pick a JS Object handler `<Name>.<method>`, pass an `args`
    // map (→ the handler's first arg), and optionally bind its return value.
    return (
      <div className="flex flex-col gap-1.5">
        <select
          aria-label="JS function"
          className={input}
          value={String(action.fn ?? "")}
          onChange={(e) => onChange({ ...action, fn: e.target.value })}
        >
          <option value="">— scegli una funzione JS —</option>
          {jsFunctions.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
        {jsFunctions.length === 0 && (
          <p className="text-[11px] leading-snug text-muted-foreground">
            Nessuna funzione JS. Aggiungi un JS Object (tab “JS” nel pannello
            Dati) ed esporta una funzione, poi selezionala qui.
          </p>
        )}
        {/* args → the handler's first argument (key → {{ }} template). */}
        <ValuesEditor
          value={action.args}
          columns={[]}
          onChange={(args) => onChange({ ...action, args })}
        />
        <input
          aria-label="Assign result to variable"
          className={input}
          list="action-var-names"
          placeholder="assegna il risultato a una variabile (opzionale)"
          value={String(action.assign ?? "")}
          onChange={(e) => onChange({ ...action, assign: e.target.value })}
        />
        <datalist id="action-var-names">
          {varNames.map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
      </div>
    );
  }
  if (SERVER_ACTION_TYPES.includes(action.type as ActionType)) {
    const needsRow = action.type === "update_row" || action.type === "delete_row";
    const needsValues = action.type === "create_row" || action.type === "update_row";
    return (
      <div className="flex flex-col gap-1.5">
        <select
          aria-label="Table"
          className={input}
          value={String(action.table ?? "")}
          onChange={(e) => onChange({ ...action, table: e.target.value })}
        >
          <option value="">— scegli una tabella —</option>
          {tables.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        {needsRow && (
          <input
            aria-label="Row id"
            className={input}
            placeholder="id riga, es. {{ $page.row }} o {{ $item.id }}"
            value={String(action.row ?? "")}
            onChange={(e) => onChange({ ...action, row: e.target.value })}
          />
        )}
        {needsValues && (
          <ValuesEditor
            value={action.values}
            columns={columnsByTable[String(action.table ?? "")] ?? []}
            onChange={(values) => onChange({ ...action, values })}
          />
        )}
      </div>
    );
  }
  // refresh — no args.
  return <p className="text-xs text-muted-foreground">Ricarica i dati della pagina.</p>;
}

/** Column → value-template map for create/update server actions. Each value
 *  is a `{{ }}` template evaluated against the page scope at click time. */
function ValuesEditor({
  value,
  columns,
  onChange,
}: {
  value: unknown;
  /** Columns of the target table — the column field is a picker, not free
   *  text (S13). Falls back to a text input only if columns are unknown. */
  columns: string[];
  onChange: (values: Record<string, string>) => void;
}) {
  // Local buffer so an in-progress blank row (empty column name) survives —
  // the persisted object drops empty keys, but the editing list keeps them.
  const [entries, setEntries] = useState<[string, string][]>(() =>
    value && typeof value === "object"
      ? Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, String(v ?? "")])
      : [],
  );

  const commit = (next: [string, string][]) => {
    setEntries(next);
    const out: Record<string, string> = {};
    for (const [k, v] of next) if (k.trim()) out[k] = v;
    onChange(out);
  };

  return (
    <div className="flex flex-col gap-1">
      <span className={fieldLabel}>Valori campi</span>
      {entries.map(([k, v], i) => (
        <div key={i} className="flex items-center gap-1">
          {columns.length > 0 ? (
            <select
              aria-label="Field column"
              className={`${input} w-1/3`}
              value={k}
              onChange={(e) => commit(entries.map((row, j) => (j === i ? [e.target.value, row[1]] : row)))}
            >
              <option value="">— colonna —</option>
              {columns.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          ) : (
            <input
              aria-label="Field column"
              className={`${input} w-1/3`}
              placeholder="colonna"
              value={k}
              onChange={(e) => commit(entries.map((row, j) => (j === i ? [e.target.value, row[1]] : row)))}
            />
          )}
          <input
            aria-label="Field value"
            className={input}
            placeholder="valore (supporta {{ }})"
            value={v}
            onChange={(e) => commit(entries.map((row, j) => (j === i ? [row[0], e.target.value] : row)))}
          />
          <button
            type="button"
            aria-label="Remove field"
            className="px-1 text-danger"
            onClick={() => commit(entries.filter((_, j) => j !== i))}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        className="w-fit rounded border border-dashed border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
        onClick={() => commit([...entries, ["", ""]])}
      >
        + Aggiungi campo
      </button>
    </div>
  );
}
